import { z } from 'zod';
import { FraudReportCategory, FraudReportStatus } from '../enums';
import { emailSchema, idSchema, paginationSchema, phoneSchema, zEnum } from './common';

/**
 * A fraud report from the public site.
 *
 * Almost every field is optional on purpose. The person filling this in has
 * often just been asked for ₹5,000 by someone on WhatsApp; they may not know the
 * company's registered name, may not have the job reference, and may not want to
 * give their own number. A form that refuses to submit without those is a form
 * that loses the report — and the report is worth more than its completeness.
 *
 * `description` is the one thing genuinely required, because a report with no
 * account of what happened cannot be triaged at all.
 */
export const fraudReportSchema = z.object({
  category: zEnum(FraudReportCategory),
  description: z
    .string()
    .trim()
    .min(20, 'Please describe what happened in a sentence or two')
    .max(4000),

  /** What the vacancy was, however the reporter can identify it. */
  jobId: idSchema.optional(),
  jobReference: z.string().trim().max(200).optional().or(z.literal('')),
  companyName: z.string().trim().max(200).optional().or(z.literal('')),

  /** Who did it, as far as the reporter knows. */
  suspectName: z.string().trim().max(120).optional().or(z.literal('')),
  suspectPhone: z.string().trim().max(20).optional().or(z.literal('')),
  /** A UPI id, bank account or wallet the money was asked to be sent to. */
  suspectPaymentId: z.string().trim().max(120).optional().or(z.literal('')),

  /**
   * Rupees, as whole numbers. Kept as strings on the wire and parsed server-side
   * so a blank field is distinguishable from zero — "they asked for nothing" and
   * "I did not say" are different facts.
   */
  amountDemanded: z.coerce.number().int().min(0).max(10_000_000).optional(),
  amountPaid: z.coerce.number().int().min(0).max(10_000_000).optional(),
  incidentAt: z.coerce.date().optional(),

  /** Blank means anonymous, which is explicitly allowed. */
  reporterName: z.string().trim().max(120).optional().or(z.literal('')),
  reporterPhone: phoneSchema.optional().or(z.literal('')),
  reporterEmail: emailSchema.optional().or(z.literal('')),

  /** Storage keys of screenshots already uploaded through the evidence endpoint. */
  evidenceKeys: z.array(z.string().trim().max(500)).max(5).default([]),
});
export type FraudReportInput = z.infer<typeof fraudReportSchema>;

/**
 * Requests an upload ticket for a screenshot attached to a report.
 *
 * Authenticated callers only — see TrustController. An anonymous endpoint that
 * mints write authority to object storage is an open file host, and the value of
 * letting a signed-out reporter attach a screenshot does not come close to
 * covering that.
 */
export const fraudEvidenceSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(3).max(120),
  sizeBytes: z.coerce.number().int().min(1),
});
export type FraudEvidenceInput = z.infer<typeof fraudEvidenceSchema>;

export const fraudReportQuerySchema = paginationSchema.extend({
  status: zEnum(FraudReportStatus).optional(),
  category: zEnum(FraudReportCategory).optional(),
  q: z.string().trim().max(120).optional(),
});
export type FraudReportQuery = z.infer<typeof fraudReportQuerySchema>;

export const fraudReportTriageSchema = z.object({
  status: zEnum(FraudReportStatus),
  /**
   * Required for a terminal status. Closing a fraud report without saying what
   * was done leaves the next reviewer — or an auditor — with a status and no
   * reasoning, which is how a queue becomes a graveyard.
   */
  resolutionNote: z.string().trim().max(2000).optional().or(z.literal('')),
});
export type FraudReportTriageInput = z.infer<typeof fraudReportTriageSchema>;
